/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { NetworkResource } from "../../model/enums";
import type { UnityRootState } from "../UnityAppStore";
import type {
  NetworkAvailabilityState,
  ResourceAvailabilityPayload,
  ResourceStatus
} from "./networkAvailability.types";

/**
 * Default resource status — unavailable, not yet evaluated.
 */
const DEFAULT_RESOURCE_STATUS: ResourceStatus = {
  isAvailable: false,
  failureReason: null
};

/**
 * Initial state for the network availability slice.
 * All resources start as unavailable with null failure reason
 * (not yet evaluated), matching the domain model's cold-start behaviour.
 */
const INITIAL_STATE: NetworkAvailabilityState = {
  networkInterface: { ...DEFAULT_RESOURCE_STATUS },
  internet: { ...DEFAULT_RESOURCE_STATUS }
};

/**
 * Maps a NetworkResource enum value to the corresponding state key.
 * @param resource - The network resource enum value
 * @returns The key on NetworkAvailabilityState, or null if unrecognised
 */
function resourceToStateKey(
  resource: NetworkResource
): keyof NetworkAvailabilityState | null {
  switch (resource) {
    case NetworkResource.NETWORK_INTERFACE:
      return "networkInterface";
    case NetworkResource.INTERNET:
      return "internet";
    default:
      return null;
  }
}

const networkAvailabilitySlice = createSlice({
  name: "networkAvailability",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Updates a single resource's availability in state.
     * Dispatched by the bridge when the domain service notifies a change.
     */
    resourceAvailabilityChanged(
      state,
      action: PayloadAction<ResourceAvailabilityPayload>
    ): void {
      const key = resourceToStateKey(action.payload.resource);
      if (key !== null) {
        state[key] = {
          isAvailable: action.payload.isAvailable,
          failureReason: action.payload.failureReason
        };
      }
    }
  }
});

// ──────────────────────────────────────────────────────────────────
// Exported actions
// ──────────────────────────────────────────────────────────────────
export const { resourceAvailabilityChanged } = networkAvailabilitySlice.actions;

// ──────────────────────────────────────────────────────────────────
// Selectors
// ──────────────────────────────────────────────────────────────────

/** Select the full network availability sub-state. */
export const selectNetworkAvailability = (
  state: UnityRootState
): NetworkAvailabilityState => state.networkAvailability;

/** Select network interface (LAN) status. */
export const selectNetworkInterfaceStatus = (
  state: UnityRootState
): ResourceStatus => state.networkAvailability.networkInterface;

/** Select Internet connectivity status. */
export const selectInternetStatus = (state: UnityRootState): ResourceStatus =>
  state.networkAvailability.internet;

/**
 * Select the status for a specific NetworkResource by enum value.
 * @param state - The root Redux state
 * @param resource - The NetworkResource to query
 * @returns The ResourceStatus, or a default unavailable status if not found
 */
export const selectResourceStatus = (
  state: UnityRootState,
  resource: NetworkResource
): ResourceStatus => {
  const key = resourceToStateKey(resource);
  if (key !== null) {
    return state.networkAvailability[key];
  }
  return DEFAULT_RESOURCE_STATUS;
};

export default networkAvailabilitySlice.reducer;
